import * as THREE from 'three';
import { CHAR_VOXEL_SIZE, Part, PARTS, type PartDef } from '@vm/shared';
import { buildCharacter, CHAR_FLAGS, CHAR_PALETTE, VoxelKind, WEAPON_BY_ID } from '@vm/content';
import { buildModelGeometry } from '../../game/render/voxelModel';
import { previewMaterials } from './stage';
import { weaponView } from './weaponModel';

const CV = CHAR_VOXEL_SIZE;

export interface MarinePart {
  def: PartDef;
  /** Rotates around the part's joint. */
  pivot: THREE.Group;
  /** Holds the part meshes, offset so the grid origin sits at the part's `min`. */
  holder: THREE.Group;
}

/**
 * A static-voxel marine for menus: the same part grids as the in-game CharacterModel
 * (buildCharacter + buildModelGeometry), without chipping, plus an optional held weapon.
 */
export class VoxelMarine {
  readonly root = new THREE.Group();
  readonly parts: MarinePart[] = [];
  private readonly geometries: THREE.BufferGeometry[] = [];
  private weapon: THREE.Group | null = null;
  private weaponKey: string | undefined;
  /** The held weapon is a melee weapon (held blade-up rather than muzzle-forward). */
  melee = false;

  /** armorId null builds the bare body (no armor shell). team -1 = neutral cyan core. */
  constructor(skin: Uint8Array, armorId: string | null, team = -1) {
    const grids = buildCharacter(skin, armorId ?? 'standard', team);
    for (const def of PARTS) {
      const grid = grids[def.id];
      let colors = grid.color;
      if (armorId === null) {
        colors = grid.color.slice();
        for (let i = 0; i < colors.length; i++) if (grid.kind[i] === VoxelKind.Armor) colors[i] = 0;
      }
      const pivot = new THREE.Group();
      pivot.position.set(def.pivot[0] * CV, def.pivot[1] * CV, def.pivot[2] * CV);
      const holder = new THREE.Group();
      holder.position.set((def.min[0] - def.pivot[0]) * CV, (def.min[1] - def.pivot[1]) * CV, (def.min[2] - def.pivot[2]) * CV);
      pivot.add(holder);
      const geo = buildModelGeometry(colors, grid.sx, grid.sy, grid.sz, CHAR_PALETTE, CHAR_FLAGS, CV);
      if (geo.lit) {
        holder.add(new THREE.Mesh(geo.lit, previewMaterials.lit));
        this.geometries.push(geo.lit);
      }
      if (geo.glow) {
        holder.add(new THREE.Mesh(geo.glow, previewMaterials.glow));
        this.geometries.push(geo.glow);
      }
      this.root.add(pivot);
      this.parts.push({ def, pivot, holder });
    }
  }

  get hasWeapon(): boolean {
    return this.weapon !== null;
  }

  /** Tilts the held weapon forward (radians) relative to the arm axis, around the grip. */
  setWeaponTilt(tilt: number): void {
    if (this.weapon) this.weapon.rotation.x = -Math.PI / 2 + tilt;
  }

  /** Puts a weapon (by weapon id, optionally camo'd) in the right hand; undefined/unknown clears it. */
  setWeapon(weaponId: string | undefined, camo?: string, scope?: string): void {
    const key = weaponId ? `${weaponId}|${camo ?? ''}|${scope ?? ''}` : undefined;
    if (key === this.weaponKey) return;
    this.weaponKey = key;
    const arm = this.parts[Part.ArmR].pivot;
    if (this.weapon) arm.remove(this.weapon);
    this.weapon = null;
    this.melee = weaponId ? WEAPON_BY_ID[weaponId]?.kind === 'melee' : false;
    // Shared, cached game geometry (weaponObject): nothing to dispose here.
    const view = weaponView(weaponId, camo, scope);
    if (!view) return;
    const g = view.object;
    // Same grip placement as CharacterModel: grip in the fist, barrel along the arm.
    g.position.set(0, -10.5 * CV, -1.2 * CV);
    g.rotation.x = -Math.PI / 2;
    arm.add(g);
    this.weapon = g;
  }

  dispose(): void {
    for (const g of this.geometries) g.dispose();
    this.geometries.length = 0;
  }
}

/** Menu idle: breathing, glancing around, weapon held forward (blade up for melee). */
export class IdlePose {
  private t = Math.random() * 50;
  private yaw = 0;
  private pitch = 0;
  private targetYaw = 0;
  private targetPitch = 0;
  private nextLook = 1.2 + Math.random() * 1.5;

  update(m: VoxelMarine, dt: number): void {
    this.t += dt;
    const breath = Math.sin(this.t * 2.1);
    const sway = Math.sin(this.t * 0.9);
    this.nextLook -= dt;
    if (this.nextLook <= 0) {
      this.nextLook = 1.8 + Math.random() * 3.4;
      if (Math.random() < 0.35) {
        this.targetYaw = 0;
        this.targetPitch = 0;
      } else {
        this.targetYaw = (Math.random() * 2 - 1) * 0.6;
        this.targetPitch = (Math.random() * 2 - 1) * 0.16 - 0.02;
      }
    }
    const k = 1 - Math.exp(-dt * 5);
    this.yaw += (this.targetYaw - this.yaw) * k;
    this.pitch += (this.targetPitch - this.pitch) * k;

    const P = m.parts;
    // The torso rocks around the hips; head and arms follow the chest so joints stay closed.
    const lean = -0.015 + breath * 0.012;
    const torso = P[Part.Torso].pivot;
    torso.rotation.set(lean, this.yaw * 0.1 + sway * 0.02, 0);
    const follow = (p: MarinePart, height: number): void => {
      const d = p.def.pivot;
      p.pivot.position.set(d[0] * CV, (12 + height * Math.cos(lean)) * CV, (d[2] + height * Math.sin(lean)) * CV);
    };
    follow(P[Part.Head], 12);
    follow(P[Part.ArmL], 10);
    follow(P[Part.ArmR], 10);
    P[Part.Head].pivot.rotation.set(this.pitch + lean, this.yaw, 0);
    if (m.hasWeapon) {
      // Blocky "holding an item" pose: forearm raised, muzzle forward (blade up for melee).
      const arm = 0.42 + breath * 0.02;
      P[Part.ArmR].pivot.rotation.set(arm, 0, -0.04);
      m.setWeaponTilt(m.melee ? 1.8 : Math.PI / 2 - arm - 0.12);
      P[Part.ArmL].pivot.rotation.set(0.08 - breath * 0.03, 0, 0.08 + breath * 0.015);
    } else {
      P[Part.ArmR].pivot.rotation.set(0.05 + breath * 0.03, 0, -0.08 - breath * 0.015);
      P[Part.ArmL].pivot.rotation.set(0.05 - breath * 0.03, 0, 0.08 + breath * 0.015);
    }
    P[Part.LegL].pivot.rotation.set(0, 0.05, 0.015);
    P[Part.LegR].pivot.rotation.set(0, -0.05, -0.015);
  }
}
